import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { AxiosResponse } from 'axios'
import { toast } from 'react-toastify'
import { severityLabelToLevel } from 'pages/Operations/Safety/Incidents/incidentsData'
import { IPaginatedResponse } from 'types/api/general.interface'
import {
  IGetAllIncidentsSingleResponse,
  IGetSingleIncidentResponse,
  IRaiseIncidentRequest,
} from 'types/api/operationsIncidentsApi.interface'
import { IIncidentsSliceInitialState, IRaiseIncidentFormState } from 'types/incidents.interface'
import { baseUrl } from 'utils/axios'
import { handleErrors } from 'utils/helpers'

const mapRaiseIncidentFormToRequest = (state: IRaiseIncidentFormState): IRaiseIncidentRequest => ({
  title: state.title,
  description: state.description || undefined,
  severityLevel: severityLabelToLevel(state.severity),
})

const initialState: IIncidentsSliceInitialState = {
  isLoading: false,
  data: [],
  totalCount: 0,
  totalPages: 0,
  singleIncident: null,
}

export const getIncidents = createAsyncThunk(
  'incidents/getIncidents',
  async (params: any, thunkAPI) => {
    try {
      const resp: AxiosResponse<IPaginatedResponse<IGetAllIncidentsSingleResponse>> =
        await baseUrl.get('aim-operations/incident', { params })
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getIncidentById = createAsyncThunk(
  'incidents/getIncidentById',
  async (id: string, thunkAPI) => {
    try {
      const { data }: AxiosResponse<IGetSingleIncidentResponse> = await baseUrl.get(
        'aim-operations/incident/' + id,
      )
      return data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const raiseIncident = createAsyncThunk(
  'incidents/raiseIncident',
  async (
    { state, params }: { state: IRaiseIncidentFormState; params?: any },
    thunkAPI,
  ) => {
    try {
      const resp: AxiosResponse<IGetSingleIncidentResponse> = await baseUrl.post(
        'aim-operations/incident',
        mapRaiseIncidentFormToRequest(state),
      )
      if (params) {
        await thunkAPI.dispatch(getIncidents(params))
      }
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

const incidentsSlice = createSlice({
  name: 'incidents',
  initialState,
  reducers: {
    clearIncidents: (state) => {
      state.data = []
      state.totalCount = 0
      state.totalPages = 0
    },
    clearSingleIncident: (state) => {
      state.singleIncident = null
    },
  },
  extraReducers: {
    [getIncidents.pending.type]: (state) => {
      state.isLoading = true
    },
    [getIncidents.fulfilled.type]: (
      state,
      { payload }: { payload: IPaginatedResponse<IGetAllIncidentsSingleResponse> },
    ) => {
      state.isLoading = false
      state.data = payload.data
      state.totalCount = payload.totalCount
      state.totalPages = payload.totalPages
    },
    [getIncidents.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [getIncidentById.pending.type]: (state) => {
      state.isLoading = true
    },
    [getIncidentById.fulfilled.type]: (
      state,
      { payload }: { payload: IGetSingleIncidentResponse },
    ) => {
      state.isLoading = false
      state.singleIncident = payload
    },
    [getIncidentById.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [raiseIncident.pending.type]: (state) => {
      state.isLoading = true
    },
    [raiseIncident.fulfilled.type]: (state) => {
      state.isLoading = false
      toast.success('Incident raised successfully')
    },
    [raiseIncident.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
  },
})

export const { clearIncidents, clearSingleIncident } = incidentsSlice.actions

export default incidentsSlice.reducer
